/**
 * Service-worker registration + lifecycle helpers — Phase PWA.
 *
 * Public API:
 *   registerServiceWorker()       — call once at app boot (no-op in dev)
 *   clearAllCachesAndUnregister() — call from logout to wipe local caches
 *
 * The "new version available" prompt is wired through a custom DOM event
 * (`pwa:update-available`) that the React tree listens for so we can render
 * a styled toast instead of a window.confirm().
 */

let _registration = null;
let _refreshing = false;

// Register on every host EXCEPT plain localhost / 127.0.0.1, where the
// HMR-driven dev server doesn't produce stable hashed bundles for the SW
// to cache.  The preview URL (and production) both register normally.
function shouldRegister() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0" || /^\[?::1\]?$/.test(host)) {
    return false;
  }
  return true;
}

export async function registerServiceWorker() {
  if (!shouldRegister()) return null;
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js");
    _registration = reg;

    // Reload all tabs once exactly when the new SW takes control.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (_refreshing) return;
      _refreshing = true;
      window.location.reload();
    });

    // Notify the app when a new version is waiting.
    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (
          installing.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          window.dispatchEvent(new CustomEvent("pwa:update-available"));
        }
      });
    });

    return reg;
  } catch (err) {
    console.error("[pwa] registration failed", err);
    return null;
  }
}

export function activateWaitingWorker() {
  if (_registration && _registration.waiting) {
    _registration.waiting.postMessage({ type: "SKIP_WAITING" });
  } else if (typeof window !== "undefined") {
    window.location.reload();
  }
}

/** Wipe every Cache Storage entry + IndexedDB + unregister all SWs.
 *
 * The SW is told to enter a "logout-purged" mode (LOGOUT_PURGE message)
 * BEFORE any cache wipe.  In that mode every strategy bypasses the cache
 * for the rest of the SW's lifetime, so the post-logout navigation to
 * `/login` (which requests `/brand/*` + `/static/*`) cannot re-populate
 * any cache after we've cleared it.
 */
export async function clearAllCachesAndUnregister() {
  // 1. Ask the SW to enter logout-purged mode + clear its caches, and
  //    WAIT for confirmation so we don't race the page-side wipe.
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    try {
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, 1500); // hard cap if SW is wedged
        const onMsg = (event) => {
          if (event.data && event.data.type === "LOGOUT_PURGE_DONE") {
            clearTimeout(timer);
            navigator.serviceWorker.removeEventListener("message", onMsg);
            resolve();
          }
        };
        navigator.serviceWorker.addEventListener("message", onMsg);
        navigator.serviceWorker.controller.postMessage({ type: "LOGOUT_PURGE" });
      });
    } catch (e) {
      /* ignore */
    }
  }
  // 2. Belt-and-braces: wipe from the page side too.  Iterate dynamically
  //    so any future cache name is automatically picked up.
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (e) {
    console.error("[pwa] cache wipe failed", e);
  }
  // 3. Unregister all SWs so the next login starts fresh.  Note: an
  //    already-controlling SW keeps controlling the current page until
  //    the next reload, but the LOGOUT_PURGE flag we set above prevents
  //    it from re-caching anything in the meantime.
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    console.error("[pwa] unregister failed", e);
  }
  // 4. Final pass — in case the not-yet-unregistered SW handled a fetch
  //    during step 2 and somehow re-created a cache, sweep again.
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (e) {
    /* ignore */
  }
  // 5. Clean any leftover IndexedDB databases.
  try {
    if (window.indexedDB && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        (dbs || []).map(
          (d) => new Promise((res) => {
            if (!d || !d.name) return res();
            const req = indexedDB.deleteDatabase(d.name);
            req.onsuccess = req.onerror = req.onblocked = () => res();
          }),
        ),
      );
    }
  } catch (e) {
    /* ignore */
  }
}

/** True iff the current page is being served by a service worker. */
export function isControlled() {
  return !!(navigator.serviceWorker && navigator.serviceWorker.controller);
}

/** True iff the page is running as an installed PWA. */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari pre-display-mode flag
  return !!window.navigator.standalone;
}
